import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminMobileNav from '../../components/admin/AdminMobileNav';
import { MapPin, Plus, Search, Trash2, Upload, ChevronLeft, ChevronRight, X, RefreshCw } from 'lucide-react';

const API = window.location.origin;

const ITEMS_PER_PAGE = 50;

export default function Locations() {
  const [locations, setLocations] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProvince, setSelectedProvince] = useState('all');
  const [newLocation, setNewLocation] = useState({ province: '', city: '', barangay: '' });
  const [showAddForm, setShowAddForm] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchProvinces = async () => {
    try {
      const res = await fetch(`${API}/api/locations/provinces`);
      const data = await res.json();
      setProvinces(data || []);
    } catch (err) {
      console.error('Error fetching provinces:', err);
    }
  };

  const fetchLocations = useCallback(async (p = page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedProvince && selectedProvince !== 'all') params.append('province', selectedProvince);
      if (searchQuery && searchQuery.trim()) params.append('q', searchQuery.trim());
      params.append('page', p.toString());
      params.append('limit', ITEMS_PER_PAGE.toString());

      const res = await fetch(`${API}/api/locations/search?${params.toString()}`);
      if (res.ok) {
        const data = await res.json();
        setLocations(data.locations || []);
        setTotalPages(data.pages || 1);
        setTotalCount(data.total || 0);
      }
    } catch (err) {
      console.error('Error fetching locations:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedProvince, searchQuery, page]);

  useEffect(() => {
    fetchProvinces();
  }, []);

  useEffect(() => {
    fetchLocations(page);
  }, [page, fetchLocations]);

  const handleSearch = () => {
    setPage(1);
    fetchLocations(1);
  };

  const handleProvinceChange = (val) => {
    setSelectedProvince(val);
    setPage(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleSeedLocations = async () => {
    try {
      const res = await fetch(`${API}/api/admin/locations/seed`, { method: 'POST', headers });
      const data = await res.json();
      if (res.ok) {
        if (data.seeded) {
          toast.success(`Location data seeded: ${data.total_barangays?.toLocaleString()} barangays, ${data.total_cities?.toLocaleString()} cities`);
        } else {
          toast.info(data.message || 'Locations already seeded. Use "Clear & Re-seed" to reload.');
        }
        fetchProvinces();
        fetchLocations(1);
      } else {
        toast.error(data.detail || 'Failed to seed locations');
      }
    } catch (err) {
      toast.error('Error seeding locations');
    }
  };

  const handleClearAndReseed = async () => {
    if (!window.confirm('This will DELETE all existing location data and re-seed from scratch. This fixes duplicate entries. Continue?')) {
      return;
    }
    try {
      // Step 1: Clear existing data
      toast.info('Clearing existing location data...');
      const clearRes = await fetch(`${API}/api/admin/locations/clear`, { method: 'DELETE', headers });
      if (!clearRes.ok) {
        toast.error('Failed to clear location data');
        return;
      }
      const clearData = await clearRes.json();
      toast.success(`Cleared ${clearData.deleted?.toLocaleString() || 0} records`);
      
      // Step 2: Re-seed
      toast.info('Seeding fresh location data (this may take a moment)...');
      const seedRes = await fetch(`${API}/api/admin/locations/seed`, { method: 'POST', headers });
      const seedData = await seedRes.json();
      if (seedRes.ok && seedData.seeded) {
        toast.success(`Re-seeded: ${seedData.total_barangays?.toLocaleString()} barangays, ${seedData.total_cities?.toLocaleString()} cities, ${seedData.total_provinces} provinces`);
        fetchProvinces();
        fetchLocations(1);
      } else {
        toast.error(seedData.detail || 'Failed to re-seed locations');
      }
    } catch (err) {
      toast.error('Error during clear & re-seed');
    }
  };

  const handleAddLocation = async (e) => {
    e.preventDefault();
    if (!newLocation.province || !newLocation.city) {
      toast.error('Province and City are required');
      return;
    }
    try {
      const params = new URLSearchParams({
        province: newLocation.province,
        city: newLocation.city,
        barangay: newLocation.barangay || 'N/A'
      });
      const res = await fetch(`${API}/api/admin/locations/add?${params.toString()}`, { method: 'POST', headers });
      if (res.ok) {
        toast.success('Location added');
        setNewLocation({ province: '', city: '', barangay: '' });
        setShowAddForm(false);
        fetchProvinces();
        fetchLocations(1);
      } else {
        const data = await res.json();
        toast.error(data.detail || data.message || 'Failed');
      }
    } catch (err) {
      toast.error('Error adding location');
    }
  };

  const handleDeleteLocation = async (location) => {
    if (!window.confirm('Delete this location?')) return;
    try {
      const params = new URLSearchParams({
        province: location.province,
        city: location.city,
        barangay: location.barangay || 'N/A'
      });
      const res = await fetch(`${API}/api/admin/locations?${params.toString()}`, { method: 'DELETE', headers });
      if (res.ok) {
        toast.success('Location deleted');
        fetchLocations(page);
      } else {
        toast.error('Failed to delete');
      }
    } catch (err) {
      toast.error('Error deleting');
    }
  };

  const goToPage = (p) => {
    const next = Math.max(1, Math.min(p, totalPages));
    setPage(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="flex min-h-screen bg-stone-50" data-testid="admin-locations-page">
      <AdminSidebar />
      <main className="flex-1 p-4 md:p-8 md:ml-64 mb-16 md:mb-0 pt-16 md:pt-4 overflow-x-hidden">
        <AdminMobileNav />

        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-[Poppins]">Location Management</h1>
            <p className="text-sm text-slate-500">{totalCount.toLocaleString()} locations across {provinces.length} provinces</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={handleSeedLocations} data-testid="seed-locations-btn">
              <Upload className="w-4 h-4 mr-2" /> Seed PH Data
            </Button>
            <Button variant="outline" onClick={handleClearAndReseed} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="clear-reseed-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> Clear & Re-seed
            </Button>
            <Button onClick={() => setShowAddForm(!showAddForm)} data-testid="add-location-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Location
            </Button>
          </div>
        </div>

        {/* Add Form */}
        {showAddForm && (
          <Card className="mb-4">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Add New Location</CardTitle>
                <button onClick={() => setShowAddForm(false)} className="text-slate-400 hover:text-slate-600">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAddLocation} className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                <Input placeholder="Province *" value={newLocation.province}
                  onChange={(e) => setNewLocation({ ...newLocation, province: e.target.value })}
                  data-testid="location-province-input" />
                <Input placeholder="City/Municipality *" value={newLocation.city}
                  onChange={(e) => setNewLocation({ ...newLocation, city: e.target.value })}
                  data-testid="location-city-input" />
                <Input placeholder="Barangay (optional)" value={newLocation.barangay}
                  onChange={(e) => setNewLocation({ ...newLocation, barangay: e.target.value })}
                  data-testid="location-barangay-input" />
                <Button type="submit" data-testid="save-location-btn">Save</Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card className="mb-4">
          <CardContent className="pt-4 pb-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input placeholder="Search province, city, or barangay..."
                  value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleKeyDown} className="pl-10 h-10"
                  data-testid="search-locations-input" />
              </div>
              <Select value={selectedProvince} onValueChange={handleProvinceChange}>
                <SelectTrigger className="w-full sm:w-[220px] h-10" data-testid="filter-province-select">
                  <SelectValue placeholder="All Provinces" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Provinces</SelectItem>
                  {provinces.map((p, idx) => (
                    <SelectItem key={idx} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={handleSearch} className="h-10" data-testid="apply-filters-btn">
                <Search className="w-4 h-4 mr-2" /> Search
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">
              Locations {totalCount > 0 && <span className="text-slate-400 font-normal">({totalCount.toLocaleString()})</span>}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900" />
              </div>
            ) : locations.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <MapPin className="w-12 h-12 mx-auto mb-3 text-slate-200" />
                <p className="font-medium text-slate-600">No locations found</p>
                <p className="text-sm">Click "Seed PH Data" to load Philippine locations</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="text-left py-2.5 px-4 font-medium text-slate-600">Province</th>
                        <th className="text-left py-2.5 px-4 font-medium text-slate-600">City/Municipality</th>
                        <th className="text-left py-2.5 px-4 font-medium text-slate-600">Barangay</th>
                        <th className="text-right py-2.5 px-4 font-medium text-slate-600 w-20">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {locations.map((loc, idx) => (
                        <tr key={loc.id || `${loc.province}-${loc.city}-${loc.barangay}-${idx}`}
                          className="border-b last:border-0 hover:bg-slate-50/60 transition-colors">
                          <td className="py-2.5 px-4 text-slate-700">{loc.province}</td>
                          <td className="py-2.5 px-4 text-slate-700">{loc.city}</td>
                          <td className="py-2.5 px-4 text-slate-500">{loc.barangay || '-'}</td>
                          <td className="py-2.5 px-4 text-right">
                            <Button variant="ghost" size="sm"
                              onClick={() => handleDeleteLocation(loc)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                              data-testid={`delete-location-${idx}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between pt-4 border-t mt-4">
                    <p className="text-xs text-slate-500">
                      Page {page} of {totalPages} ({totalCount.toLocaleString()} total)
                    </p>
                    <div className="flex items-center gap-1">
                      <Button variant="outline" size="sm" onClick={() => goToPage(page - 1)}
                        disabled={page === 1} className="h-8 px-2.5" data-testid="locations-prev-page">
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let p;
                        if (totalPages <= 5) p = i + 1;
                        else if (page <= 3) p = i + 1;
                        else if (page >= totalPages - 2) p = totalPages - 4 + i;
                        else p = page - 2 + i;
                        return (
                          <button key={p} onClick={() => goToPage(p)}
                            className={`w-8 h-8 rounded text-xs font-medium transition-colors ${
                              page === p ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
                            }`}>
                            {p}
                          </button>
                        );
                      })}
                      <Button variant="outline" size="sm" onClick={() => goToPage(page + 1)}
                        disabled={page === totalPages} className="h-8 px-2.5" data-testid="locations-next-page">
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
